/**
 * Collection Vue Component
 * ------------------------------------------------------------------------------
 * Template in sections/collection.liquid
 *
 */

import Vue from "vue";
import debounce from "lodash-es/debounce";

import ProductItem from "./product-item.js";
import Collapsible from "./collapsible.vue";
import VueSlider from "vue-slider-component";

import { fetchStorefrontNodes } from "../utils/api";
import { getElementOffset, composeProductImageUrl } from "./../utils/helpers";
import { transformSearchspringProduct } from '../utils/data.js';
import { fetchProductResults } from '../vendors/searchspring.js';

const PRICE_KEY = "ss_price";

export default {
  components: {
    ProductItem,
    Collapsible,
    VueSlider,
  },

  props: {
    collectionHandle: {
      type: String,
      default: "all",
    },

    searchTerms: {
      type: String,
    },

    rawFileUrl: {
      type: String,
      default: null,
    },

    filterConfig: {
      type: Array,
    },

    defaultProductPerPage: {
      type: Number,
    },

    pageOptionType: {
      type: String,
      default: null,
    },

    sortOptions: {
      type: Array
    },
  },

  data() {
    return {
      isLoading: false,
      isMoreProductsLoading: false,
      paginateBy: this.defaultProductPerPage,
      currentPage: 1,
      pageCount: 0,
      filterData: [],
      selectedFilters: {},
      showFilter: false,
      isSortActive: false,
      priceRange: [],
      minPrice: null,
      maxPrice: null,
      sortBy: null,
      products: [],
      productCount: 0,
      previewImageToggles: {},

      searchspringHeaderBanners: [],
      searchspringSecondaryBanners: [],
      searchspringFooterBanners: [],
      searchspringTag: null,
    };
  },

  computed: {
    priceFilter() {
      return this.filterData.find((filter) => filter.key === PRICE_KEY);
    },
    
    parsedFileUrl() {
      return this.rawFileUrl.replace(/[^\/]+$/, "").substring(0, this.rawFileUrl.lastIndexOf("files"));
    },

    isFilterApplied() {
      let isApplied = false;
      for (const filter in this.selectedFilters) {
        if (this.selectedFilters[filter].length > 0) {
          isApplied = true;
          continue;
        }
      }

      return isApplied;
    },
  },

  watch: {
    isLoading(value) {
      if (!value) {
        this.$nextTick(() => {
          this._getTitleHeight();
        });
      }
    },

    showFilter(value) {
      value ? document.querySelector("body").classList.add("no-scroll") : document.querySelector("body").classList.remove("no-scroll");
    },
  },

  mounted() {
    // Load filters and sort from URL params.
    const params = new URL(window.location).searchParams;

    // Sort
    for (const sort of this.sortOptions) {
      if (!sort.key) continue;

      if (params.get(`sort.${sort.key}`) === sort.direction) {
        this.sortBy = sort;
      }
    }

    // Pagination
    if (params.get("page")) {
      this.currentPage = params.get("page");
    }

    // Filters
    for (const filter of this.filterConfig) {
      if (filter.key === PRICE_KEY) {
        const low = params.get(`filter.${filter.key}.low`);
        const high = params.get(`filter.${filter.key}.high`);
        if (low || high) {
          Vue.set(this.selectedFilters, filter.key, [low, high]);
        }

      } else {
        const filterValues = params.getAll(`filter.${filter.key}`);
        if (filterValues.length) {
          Vue.set(this.selectedFilters, filter.key, filterValues);
        }
      }
    }

    // Searchspring landing page - 
    // https://docs.searchspring.com/reference/advanced-merchandising#landing-page-campaign
    if (params.get("tag")) {
      this.searchspringTag = params.get("tag");
    }

    this._getProducts();

    window.addEventListener("resize", debounce(this._getTitleHeight, 100));
  },

  directives: {
    clickOutside: {
      bind: function (el, binding, vnode) {
        window.event = function (event) {
          if (!(el == event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };

        document.body.addEventListener("click", window.event);
      },
      unbind: function (el) {
        document.body.removeEventListener("click", window.event);
      },
    },

    infiniteScroll: {
      bind(el, binding, vnode) {
        window.addEventListener(
          "scroll",
          debounce(function () {
            // Return when loading or total product count is equal or smaller than products
            if (vnode.context.productCount <= vnode.context.products.length || vnode.context.isLoading || vnode.context.isMoreProductsLoading) return;

            // First check if pagination should trigger.
            const elementBottom = el.scrollHeight + getElementOffset(el).top;
            const hasReachedBottom = window.scrollY + window.innerHeight > elementBottom;

            // Then add pagination amount to the number of items to show.
            // This automatically shows more items based on the computed value.
            if (hasReachedBottom && vnode.context.pageOptionType == "infinite_scroll") {
              vnode.context.currentPage += 1;
              vnode.context._getProducts(true);
            }
          }, 100)
        );
      },
    },
  },

  methods: {
    toggleSort() {
      this.isSortActive = !this.isSortActive;
    },

    handleBodyClick() {
      this.isSortActive = false;
    },

    updatePage(page) {
      this.currentPage = page;
      this._getProducts();
    },

    updateProducts() {
      this.currentPage = 1;
      this._getProducts();
    },

    handleClickLoadMore() {
      if (this.productCount > this.products.length) {
        this.currentPage += 1;
        this._getProducts(true);
      }
    },

    priceUpdated: debounce(function () {
      if (this.priceRange[1] != this.maxPrice || this.priceRange[0] != this.minPrice) {
        this.selectedFilters[PRICE_KEY] = this.priceRange;
      } else {
        this.selectedFilters[PRICE_KEY] = [];
      }

      this.currentPage = 1;
      this._getProducts();
    }, 1000),

    // Set URL params to persist filter selections
    _updateUrlParams(filterSummary=[]) {
      const url = new URL(window.location);
      const params = url.searchParams;

      // Sort
      for (const sort of this.sortOptions) {
        params.delete(`sort.${sort.key}`);
      }

      if (this.sortBy?.key) {
        params.set(`sort.${this.sortBy.key}`, this.sortBy.direction);
      }

      // Pagination
      if (this.currentPage > 1 && this.pageOptionType === 'pagination') {
        params.set('page', this.currentPage);
      } else {
        params.delete('page');
      }

      // Filters
      for (const filter of this.filterData) {
        if (filter.key === PRICE_KEY) {
          params.delete(`filter.${filter.key}.low`);
          params.delete(`filter.${filter.key}.high`);
        } else {
          params.delete(`filter.${filter.key}`);
        }
      }

      for (const {field, value} of filterSummary) {
        if (typeof value === 'object' && value.rangeHigh !== undefined) {
          params.set(`filter.${field}.low`, value.rangeLow);
          params.set(`filter.${field}.high`, value.rangeHigh);

        } else {
          params.append(`filter.${field}`, value);
        }
      }

      window.history.replaceState(null, null, `${url.origin}${url.pathname}${params.size ? '?' : ''}${params.toString()}`);
    },

    clearFilters() {
      if (this.isFilterApplied) {
        for (let filter of this.filterData) {
          this.selectedFilters[filter.key] = [];
        }

        this.priceRange = [this.minPrice, this.maxPrice];
        this._updateUrlParams();
        this.updateProducts();
      }
    },

    // Searchspring Search API -
    // https://docs.searchspring.com/reference/get-search
    // Using facetFilters attribute for everything except for price
    // Using numericFilters for price
    _getFilterAttributes(preloadProducts) {
      const params = new URLSearchParams();
      params.set('bgfilter.ss_available', 1);
      params.set('resultsPerPage', this.paginateBy);
      params.set('page', this.currentPage);

      if (this.searchspringTag) {
        params.set('tag', this.searchspringTag);
      } else if (this.searchTerms) {
        params.set('q', this.searchTerms);
      } else {
        params.set('bgfilter.collection_handle', this.collectionHandle);
      }

      if (this.sortBy) {
        params.set(`sort.${this.sortBy.key}`, this.sortBy.direction);
      }

      for (const filter of this.filterConfig) {
        if (!this.selectedFilters[filter.key]?.length) continue;

        if (filter.key === PRICE_KEY) {
          params.set('filter.ss_price.low', this.selectedFilters[PRICE_KEY][0]);
          params.set('filter.ss_price.high', this.selectedFilters[PRICE_KEY][1]);

        } else {
          for (const selectedValue of this.selectedFilters[filter.key]) {
            params.append(`filter.${filter.key}`, selectedValue);
          }
        }
      }

      if (preloadProducts) {
        params.set('resultsPerPage', this.currentPage * this.paginateBy);
      }

      return params;
    },

    // If we pass triggerLoadMore = true, won't update filters data but only products
    // preloadProducts will preload the products that are already loaded, used when changing sort
    async _getProducts(triggerLoadMore=false, preloadProducts=false) {
      const params = this._getFilterAttributes(preloadProducts);

      // Store products from previous call in previousProducts
      let previousProducts = null;
      if (triggerLoadMore) {
        previousProducts = this.products;
        this.isMoreProductsLoading = true;
      } else {
        this.isLoading = true;
      }

      const results = await fetchProductResults({}, params.toString());
      let productHits = results.results?.map(product => transformSearchspringProduct(product));

      // Get all the sibling IDs to call graphql product id query
      const productGraphqlIds = productHits.map(({id}) => '"' + btoa("gid://shopify/Product/" + id) + '"');
      const storefrontProductQuery = `
        ... on Product {
          id
          images(first: 10) {
            edges {
                node {
                    url
                }
            }
          }
          siblingIds: metafield(namespace: "product.info", key: "sibling_ids") {
            value
          }
        }
      `;
      const storefrontProductNodes = await fetchStorefrontNodes(storefrontProductQuery, productGraphqlIds);

      // Adding siblingIds to products and check where siblings belong based on the list
      for (const node of storefrontProductNodes) {
        if (!node) continue;

        const id = node.id.replace("gid://shopify/Product/", "");
        const product = productHits.find(product => product.id === id);
        if (!product) continue;
        product.images = node.images?.edges.map(edge => edge.node.url);

        if (node.siblingIds) {
          const siblingIds = node.siblingIds.value.split("|");
          product.siblingIds = siblingIds;
          product.siblings = [];
        }
      }

      const allSiblingIds = productHits
        .map(({siblingIds}) => siblingIds)
        .flat()
        .filter((id) => id);
      const uniqueSiblingIds = [...new Set(allSiblingIds)];
      const siblingGraphqlIds = uniqueSiblingIds.map(id => '"' + btoa("gid://shopify/Product/" + id) + '"');
      const storefrontSiblingQuery = `
        ... on Product {
          id
          title
          handle
          availableForSale
          images(first: 1) {
              edges {
                  node {
                      url
                  }
              }
          }
          colourHex: metafield(namespace: "product.info", key: "colour_hex") {
            value
          }
        }
      `;
      const storefrontSiblingNodes = await fetchStorefrontNodes(storefrontSiblingQuery, siblingGraphqlIds);

      // If the node id exists in the list of siblingIds, add the node to siblings property in products
      for (const node of storefrontSiblingNodes) {
        if (!node) continue;

        const id = node.id.replace("gid://shopify/Product/", "");
        const products = productHits.filter((product) => product.siblingIds?.includes(id));

        for (const product of products) {
          const sibling = {
            ...node,
            id: parseInt(node.id.replace("gid://shopify/Product/", "")),
            available: node.availableForSale,
            images: node.images.edges.map((edge) => edge.node.url),
            colourHex: node.colourHex?.value
          }
          product.siblings.push(sibling);
        }
      }

      this._getPreviewImageToggles(productHits);

      this.products = productHits;

      // Insert Searchspring inline banners - 
      // https://docs.searchspring.com/reference/advanced-merchandising#inline-banners
      for (const banner of results.merchandising.content.inline || []) {
        this.products.splice(banner.config.position.index, 0, {
          id: banner.config.position.index,
          banner: banner.value
        });
      }

      if (previousProducts) {
        this.products = [...previousProducts, ...this.products];
      }

      this.productCount = results.pagination.totalResults;
      this.pageCount = results.pagination.totalPages;

      // Searchspring banners - 
      // https://docs.searchspring.com/reference/standard-merchandising#banners
      this.searchspringHeaderBanners = results.merchandising.content.header || [];
      this.searchspringSecondaryBanners = results.merchandising.content.banner || [];
      this.searchspringFooterBanners = results.merchandising.content.footer || [];

      if (!triggerLoadMore) {
        // Get facets
        for (const filter of this.filterConfig) {
          const facet = results.facets.find(facet => filter.key === facet.field);
          if (!facet) continue;

          if (facet.field !== PRICE_KEY) {
            const data = {
              title: filter.title,
              key: filter.key,
              options: [],
            };

            let options = facet.values.map(({value}) => value);

            // Sort non size options alphabetically
            // Sort size options by number and gender/category
            if (!facet.field.includes("size")) {
              options.sort();
            } else {
              const womenSizes = this._sortSizes(options.filter((option) => option.toLowerCase().includes("w")));
              const youthSizes = this._sortSizes(options.filter((option) => option.toLowerCase().includes("y")));
              const childSizes = this._sortSizes(options.filter((option) => option.toLowerCase().includes("c")));
              const menSizes = this._sortSizes(options.filter((option) => !isNaN(Number(option))));

              options = [...menSizes, ...womenSizes, ...youthSizes, ...childSizes];
            }

            data.options = options;

            // If there is overlapping filter data, overwrite it
            let existingFilter = this.filterData.find(({key}) => key === facet.field);
            if (existingFilter) {
              existingFilter = data;
            } else {
              this.filterData.push(data);
            }

            if (!this.selectedFilters?.[facet.field]) {
              Vue.set(this.selectedFilters, facet.field, []);
            }

          } else { // Price facet
            // Create price filter differently instead of having array options, add min/max values
            if (!this.filterData.find((data) => data.key === PRICE_KEY)) {
              let priceData = {
                title: filter.title,
                key: filter.key,
                options: {
                  min: facet.range?.[0] || 0,
                  max: facet.range?.[1] || 0,
                },
              };

              this.filterData.push(priceData);

              if (!this.selectedFilters?.[PRICE_KEY]) {
                Vue.set(this.selectedFilters, PRICE_KEY, []);
              }
            }

            // Get price range facet
            if (this.priceFilter) {
              this.minPrice = this.priceFilter.options.min;
              this.maxPrice = this.priceFilter.options.max;

              this.priceRange = this.selectedFilters[PRICE_KEY].length ? [Math.floor(parseFloat(this.selectedFilters[PRICE_KEY][0])), Math.ceil(parseFloat(this.selectedFilters[PRICE_KEY][1]))] : [this.minPrice, this.maxPrice];
            }
          }
        }

        window.scrollTo(0, 0);
      }

      this._updateUrlParams(results.filterSummary);

      this.isLoading = false;
      this.isMoreProductsLoading = false;
      this._getTitleHeight();
    },

    _getPreviewImageToggles(products) {
      for (const product of products) {
        if (product.siblings) {
          product.siblings.forEach((sibling) => {
            Vue.set(this.previewImageToggles, `${product.handle}_${sibling.handle}`, false);
          });
        }
      }
    },

    _getTitleHeight() {
      if (this.$refs?.productEls?.length) {
        const heights = this.$refs.productEls.map((productEl) => productEl.querySelector(".product-item__title")?.clientHeight);
        const highest = Math.max(...heights);
        this.$refs.productEls.forEach((productEl) => productEl.querySelector(".product-item__title--wrapper") && (productEl.querySelector(".product-item__title--wrapper").style.minHeight = `${highest}px`));
        this.$refs.productEls.forEach((productEl) => productEl.querySelector(".product-item__title") && (productEl.querySelector(".product-item__title").style.minHeight = `${highest}px`));
      }
    },

    imageUrlPrefix() {
      return composeProductImageUrl();
    },

    _sortSizes(options) {
      const regex = /[+-]?\d+(\.\d+)?/g;
      return options.sort((a, b) => parseFloat(a.match(regex)) - parseFloat(b.match(regex)));
    },
  },
};
